import { FC, useContext, useMemo } from 'react';
import { BoardContext } from '../../../contexts/BoardContext';
import { AppContext } from '../../../App';
import { SEO } from '../../Helmet/HelmetSEO';
import TaskAddEditForm, { ITaskAddEditFormProps } from '../../Task/TaskAddEditForm/TaskAddEditForm';
import BoardMenu from '../BoardMenu/BoardMenu';
import BoardToolbar from '../BoardToolbar/BoardToolbar';
import BoardColumns from '../BoardColumns/BoardColumns';
import ConfirmationWindow from '../../Shared/ConfirmationWindow/ConfirmationWindow';
import "./BoardMain.css";

const BoardMain: FC = () => {
    const boardContext = useContext(BoardContext);
    const { pageTitle } = useContext(AppContext);

    const seoTitle = useMemo(() => 
        boardContext.boardName 
            ? `${boardContext.boardName} | Доска · ${pageTitle}`
            : pageTitle,
        [boardContext.boardName, pageTitle]
    );

    const taskFormProps = useMemo((): ITaskAddEditFormProps => ({
        responsibleRequired: boardContext.projectSettings.responsibleRequired,
        mode: "create",
        taskId: undefined,
        closeFunc: () => boardContext.setShowTaskPopup(false),
        columnId: boardContext.addTaskColumnId,
        onTaskAction: boardContext.handleTaskAction,
        projectId: boardContext.projectId,
        optionsQueryParams: {
            page: "board",
            entity: "task-form",
            action: "create",
            entityId: undefined
        },
        defaultBoardId: boardContext.boardIdNumber
    }), [boardContext]);

    const deleteBoardProps = useMemo(() => ({
        headerText: "Удаление доски",
        bodyText: "Вы уверены, что хотите удалить доску?",
        confirmText: "Да",
        cancelText: "Нет",
        onConfirm: boardContext.deleteBoard,
        onCancel: () => boardContext.setShowConfirmDeleteBoard(false)
    }), [boardContext.deleteBoard, boardContext.setShowConfirmDeleteBoard]);

    const deleteColumnProps = useMemo(() => ({
        headerText: "Удаление колонки",
        bodyText: "Вы уверены, что хотите удалить колонку? Все задачи в колонке будут удалены",
        confirmText: "Да",
        cancelText: "Нет",
        onConfirm: boardContext.deleteColumn,
        onCancel: () => boardContext.setShowConfirmDeleteColumn(false)
    }), [boardContext.deleteColumn, boardContext.setShowConfirmDeleteColumn]);

    const deleteTasksProps = useMemo(() => ({
        headerText: "Удаление задачи",
        bodyText: "Вы уверены, что хотите удалить задачи?",
        confirmText: "Да",
        cancelText: "Нет",
        onConfirm: boardContext.deleteTasks,
        onCancel: () => boardContext.setShowConfirmDeleteTasks(false)
    }), [boardContext.deleteTasks, boardContext.setShowConfirmDeleteTasks]);

    const toolbarProps = useMemo(() => ({
        onMassActionsFunc: boardContext.cancelActionMode,
        selectedCount: boardContext.selectedTasks.length,
        userAccess: boardContext.userAccess
    }), [boardContext.cancelActionMode, boardContext.selectedTasks.length, boardContext.userAccess]);

    return (
        <>
            <SEO title={seoTitle} />
            {boardContext.showTaskPopup && (
                <div className="full-screen-overlay">
                    <div className="popup-form-background" onClick={(e) => e.stopPropagation()}>
                        <TaskAddEditForm {...taskFormProps} />
                    </div>
                </div>
            )}
            {boardContext.showConfirmDeleteBoard && (
                <ConfirmationWindow {...deleteBoardProps} />
            )}
            {boardContext.showConfirmDeleteColumn && (
                <ConfirmationWindow {...deleteColumnProps} />
            )}
            {boardContext.showConfirmDeleteTasks && (
                <ConfirmationWindow {...deleteTasksProps} />
            )}
            <div className="board__container">
                <div className="toolbar-container">
                    <BoardToolbar {...toolbarProps} />
                </div>
                <div className="board__container--columns">
                    <BoardColumns userAccess={boardContext.userAccess} />
                </div>
            </div>
            {boardContext.showMenu && (
                <div>
                    <BoardMenu userAccess={boardContext.userAccess} />
                </div>
            )}
        </>
    );
};

export default BoardMain;